<script setup lang="ts">
/**
 * What stands in for the application while there is no session.
 *
 * Two states, and the difference between them is the whole point.
 *
 * **Still trying.** A line saying so, and nothing to press. Somebody arriving
 * without a portal session is on their way to the portal; an offer to take
 * them there would be a button whose only answer is yes, shown to somebody
 * already going.
 *
 * **Given up.** Then, and only then, something to read: the portal could not
 * be reached, which is a fact worth putting on the screen, with the one action
 * that can change it. The alternative, a frame that stays quiet forever, is
 * indistinguishable from an application that has crashed.
 */
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

defineProps<{
  /** Whether a session is still being sought. */
  establishing: boolean
  /** Why it could not be had, when the reason is known. */
  problem: string | null
}>()

/**
 * Start over from the top.
 *
 * A reload rather than a second call into the sign-in code: boot already knows
 * every way a session can be had, embedded and standalone, and running it
 * again is exactly what is wanted here. Anything cleverer would be a second
 * copy of that decision, kept in step by hand.
 */
function retry(): void {
  window.location.reload()
}
</script>

<template>
  <div class="gate">
    <div v-if="establishing" class="gate__waiting">
      <span class="gate__spinner" aria-hidden="true" />
      <p class="gate__line">{{ t('session.connecting') }}</p>
    </div>

    <div v-else class="gate__panel" role="alert">
      <h1 class="gate__title">{{ t('session.blockedTitle') }}</h1>
      <p class="gate__body">{{ problem ?? t('session.unreachableBody') }}</p>
      <button type="button" class="button-amelkis" @click="retry">
        {{ t('session.retry') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.gate {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
}

.gate__waiting {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.gate__spinner {
  width: 22px;
  height: 22px;
  border: 2px solid var(--hairline-strong, rgb(0 0 0 / 12%));
  border-top-color: var(--accent, currentcolor);
  border-radius: 50%;
  animation: gate-spin 0.7s linear infinite;
}

/* A ring that never turns is a ring that looks broken. Nothing is lost by
   showing the words alone to somebody who has asked for less movement. */
@media (prefers-reduced-motion: reduce) {
  .gate__spinner {
    display: none;
  }
}

@keyframes gate-spin {
  to {
    transform: rotate(1turn);
  }
}

.gate__line {
  margin: 0;
  color: var(--color-text-muted);
}

.gate__panel {
  max-width: 28rem;
  padding: 2rem;
  text-align: center;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg, 8px);
}

.gate__title {
  margin: 0 0 0.75rem;
  font-size: 1.25rem;
  color: var(--color-text);
}

.gate__body {
  margin: 0 0 1.5rem;
  color: var(--color-text-muted);
}
</style>
